import { isEmpty } from 'radash';
import { useController } from 'react-hook-form';
import { useShallow } from 'zustand/react/shallow';

import { Input } from '@/components/core/Input';

import useFocusOnAttribute from '../hooks/useFocusOnAttribute';
import { useIsAttributeReadonly } from '../hooks/useIsAttributeReadonly';
import { useIsHiddenAttribute } from '../hooks/useIsHiddenAttribute';
import { usePlaceholder } from '../hooks/usePlaceholder';
import { useValidationsStore, Validation } from '../hooks/useValidationsStore';
import { Layout } from '../Layout/Layout';
import { EditorComponent } from '../types';

/**
 * Temporary editor: Asset() is edited as a plain text field (DAM asset primary key).
 * Proper picker / linking UX will replace this later.
 */
const AssetComponent = ({
    attribute,
    direction = 'horizontal',
    fieldPath = attribute.name,
    gridMode = false,
    hidden = false,
    readonly = false,
    hideLabel = false,
    isKeyAttribute = false,
    beforeAttributeControl,
    hasErrors: parentHasErrors = false,
    hasWarnings: parentHasWarnings = false,
    handleUrlChange,
    labelClassName,
    onBlur: parentOnBlur,
}: EditorComponent) => {
    const focusRef = useFocusOnAttribute<HTMLInputElement>(attribute);

    const placeholder = usePlaceholder(attribute, readonly);

    const validationErrors = useValidationsStore(useShallow(state => state.validationErrors[fieldPath] as Validation[]));
    const validationWarnings = useValidationsStore(useShallow(state => state.validationWarnings[fieldPath] as Validation[]));
    const hasErrors = parentHasErrors || !isEmpty(validationErrors);
    const hasWarnings = parentHasWarnings || !isEmpty(validationWarnings);

    hidden = useIsHiddenAttribute(attribute, attribute.name, hidden);
    readonly = useIsAttributeReadonly(attribute, attribute.name, readonly);

    const { field } = useController({
        name: fieldPath,
        defaultValue: '',
    });

    const setRef = (e: HTMLInputElement | null) => {
        if (e) {
            (focusRef as React.MutableRefObject<HTMLInputElement | null>).current = e;
        }
        field.ref(e);
    };

    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
        requestAnimationFrame(() => {
            const input = e.target;
            if (input && document.activeElement === input) {
                const length = input.value.length;
                input.setSelectionRange(length, length);
            }
        });
    };

    const handleBlur = () => {
        field.onBlur();
        if (parentOnBlur) {
            parentOnBlur();
        }
    };

    if (hidden) {
        return null;
    }

    return (
        <Layout
            attribute={attribute}
            direction={direction}
            fieldPath={fieldPath}
            gridMode={gridMode}
            hideLabel={hideLabel}
            isKeyAttribute={isKeyAttribute}
            beforeAttributeControl={beforeAttributeControl}
            isClearable={!gridMode && !readonly}
            labelClassName={labelClassName}
        >
            <Input
                id={fieldPath}
                borderless={gridMode}
                placeholder={placeholder}
                disabled={readonly}
                onClick={handleUrlChange}
                {...field}
                onBlur={handleBlur}
                onFocus={handleFocus}
                ref={setRef}
                gridMode={gridMode}
                className={`w-full tw-h-[36px] ${hasErrors ? 'error-input' : hasWarnings ? 'warning-input' : ''} tw-bg-white`}
            />
        </Layout>
    );
};

export { AssetComponent };
